"use client";

/**
 * CTA Section V2 - Compelling call-to-action for booking
 */

import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Sparkles } from "lucide-react";

interface CTAV2Props {
  tenantName: string;
  onBookingClick?: () => void;
}

export function CTAV2({ tenantName, onBookingClick }: CTAV2Props) {
  const scrollToServices = () => {
    const servicesSection = document.getElementById("servicos");
    servicesSection?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative py-20 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-background">
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-20" />
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 h-20 w-20 rounded-full bg-primary/20 blur-2xl animate-pulse" />
        <div
          className="absolute bottom-10 right-10 h-32 w-32 rounded-full bg-primary/20 blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-1/3 h-24 w-24 rounded-full bg-primary/10 blur-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          {/* Icon */}
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>

          {/* Heading */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            Tudo pronto para agendar seu horário?
          </h2>

          {/* Description */}
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Agende agora mesmo com{" "}
            <span className="font-semibold text-foreground">{tenantName}</span>{" "}
            e garanta seu atendimento de forma rápida e fácil.
          </p>

          {/* Benefits */}
          <div className="flex flex-wrap justify-center gap-4 py-4">
            {[
              "✨ Agendamento Instantâneo",
              "📱 Confirmação por WhatsApp",
              "⏰ Horários Flexíveis",
            ].map((benefit) => (
              <div
                key={benefit}
                className="px-4 py-2 rounded-full bg-background/80 backdrop-blur text-sm font-medium shadow-sm"
              >
                {benefit}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all group"
              onClick={() =>
                onBookingClick ? onBookingClick() : scrollToServices()
              }
            >
              <Calendar className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              Agendar Agora
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="px-8 py-6 text-lg font-semibold bg-background/80 backdrop-blur"
              onClick={scrollToServices}
            >
              Ver Serviços
            </Button>
          </div>

          {/* Trust badge */}
          <p className="text-sm text-muted-foreground pt-4">
            🔒 Seus dados estão seguros conosco
          </p>
        </div>
      </div>
    </section>
  );
}
